/**
 * Deterministic US address normalization. Applied BEFORE hashing and BEFORE
 * any provider call so that (a) the cache key is stable across cosmetic
 * variations ("123 Main St." === "123 MAIN STREET"), maximizing hit rate,
 * and (b) providers see a clean, USPS-canonical string. US-only in M2 —
 * international grammars are deferred.
 *
 * Pure and isomorphic (no node:crypto / node-only APIs) so browser/client code
 * — e.g. M3's row_key derivation — can import it directly. The server-only
 * cache-key hash lives in ./cacheKey.ts.
 *
 * Output is uppercase USPS-style: full suffix/directional/unit words folded
 * to their canonical abbreviations, punctuation stripped, whitespace collapsed.
 */

// USPS C1 street-suffix full-word → canonical abbreviation (common subset).
const SUFFIXES: Record<string, string> = {
  STREET: "ST",
  STR: "ST",
  AVENUE: "AVE",
  AV: "AVE",
  BOULEVARD: "BLVD",
  DRIVE: "DR",
  ROAD: "RD",
  LANE: "LN",
  COURT: "CT",
  PLACE: "PL",
  CIRCLE: "CIR",
  TERRACE: "TER",
  PARKWAY: "PKWY",
  HIGHWAY: "HWY",
  SQUARE: "SQ",
  TRAIL: "TRL",
  WAY: "WAY",
  PLAZA: "PLZ",
  CROSSING: "XING",
  POINT: "PT",
  RIDGE: "RDG",
};

// Directionals full-word → abbreviation.
const DIRECTIONALS: Record<string, string> = {
  NORTH: "N",
  SOUTH: "S",
  EAST: "E",
  WEST: "W",
  NORTHEAST: "NE",
  NORTHWEST: "NW",
  SOUTHEAST: "SE",
  SOUTHWEST: "SW",
};

// Secondary-unit designators → USPS abbreviation.
const UNITS: Record<string, string> = {
  APARTMENT: "APT",
  APT: "APT",
  SUITE: "STE",
  STE: "STE",
  UNIT: "UNIT",
  BUILDING: "BLDG",
  BLDG: "BLDG",
  FLOOR: "FL",
  FL: "FL",
  ROOM: "RM",
  DEPARTMENT: "DEPT",
};

function foldToken(token: string): string {
  return SUFFIXES[token] ?? DIRECTIONALS[token] ?? UNITS[token] ?? token;
}

/**
 * Normalize a free-form address into the canonical uppercase form used for
 * caching and provider calls. Returns `""` for empty/whitespace input.
 */
export function normalizeAddress(raw: string): string {
  if (!raw) return "";
  const upper = raw
    .toUpperCase()
    // Drop punctuation that carries no addressing meaning; keep alphanumerics,
    // spaces, and the unit marker '#'. Commas/periods/etc. become spaces.
    .replace(/[^A-Z0-9#\s]/g, " ")
    // Keep '#' attached to its number: "# 4" → "#4".
    .replace(/#\s+/g, "#");

  const tokens = upper.split(/\s+/).filter(Boolean).map(foldToken);
  return tokens.join(" ").trim();
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * True for a canonical UUID string. The user-scoped (Google) cache lane is
 * keyed by a `users.id` UUID; a non-UUID owner (anon session id, org slug,
 * email) must never reach that lane — Postgres rejects it and, more to the
 * point, such an account has no business in the per-user lane.
 */
export function isUuid(v: string | null | undefined): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}

/**
 * Detect a usable pre-supplied coordinate pair. Rows carrying valid lat/lng
 * pass straight through Geo Core with no provider call and no metering.
 * US-scope sanity bounds are intentionally generous (includes AK/HI + a
 * margin) but reject obvious junk / (0,0) null-island.
 */
export function isValidLatLng(lat: unknown, lng: unknown): lat is number {
  if (typeof lat !== "number" || typeof lng !== "number") return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat === 0 && lng === 0) return false; // null island — treat as missing
  return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}
